import React, { useEffect, useState, useRef } from 'react'
import { Loader2, Save, ImageIcon, Upload, X, Trash2, Plus, DollarSign, Eye } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import { Textarea } from '@/app/components/ui/textarea'
import { Button } from '@/app/components/ui/button'
import { Checkbox } from '@/app/components/ui/checkbox'
import { FormError } from '../../ui/FormError'
import { CatalogSelect } from '../../ui/CatalogSelect'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { toast } from 'sonner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs'
import {
  calculateFinalPrice,
  calculateSalePrice,
  recalculateCosts,
  calculateTierPrices,
} from '../../../services/productCalculations.service'
import { taxService } from '@/app/services/inventory.service'
import { productService, ProductDetail, UpdateProductPayload } from '@/app/services/product.service'
import { DiscreteLoader } from '../../ui/discrete-loader'
import { ImageFile } from '@/app/services/product.service'
import { ImagePreviewModal } from '../../ui/ImagePreviewModal'


// ─── Helper: crear objeto ImageFile ─────────────────────────────────────────
function createImageFile(file: File, sortOrder?: number): ImageFile {
  return { file, previewUrl: URL.createObjectURL(file), sortOrder }
}

// ─── Esquema de validación ───────────────────────────────────────────────────
const editSchema = z.object({
  name: z.string().min(4, 'El nombre es requerido'),
  barcode: z.string().min(1, 'El código de barras es requerido'),
  description: z.string().optional(),
  categoryId: z.string().min(1, 'La categoría es requerida'),
  departmentId: z.string().min(1, 'El departamento es requerido'),
  productTypeId: z.string().min(1, 'El tipo de producto es requerido'),
  unitId: z.string().min(1, 'La unidad es requerida'),
  satProductKeyId: z.string().optional(),
  costWithoutTaxes: z.string().min(1, 'El costo es requerido'),
  costWithTaxes: z.string().optional(),
  profitPercentage: z.string().min(1, 'El porcentaje de ganancia es requerido'),
  taxId: z.string().optional(),
  is_active: z.boolean().optional(),
  pricingTiers: z.array(z.any()).optional(),
})

type EditFormValues = z.infer<typeof editSchema>

interface EditProductModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: number | null
  onSuccess?: () => void
}

export function EditProductModal({
  open,
  onOpenChange,
  productId,
  onSuccess,
}: EditProductModalProps) {
  // ── Imagen hero ──────────────────────────────────────────────────────────
  const [heroImage, setHeroImage] = useState<ImageFile | null>(null)
  const [heroImageExisting, setHeroImageExisting] = useState<string | null>(null)
  const [removeExistingHero, setRemoveExistingHero] = useState(false)

  // ── Galería ──────────────────────────────────────────────────────────────
  const [galleryNew, setGalleryNew] = useState<ImageFile[]>([])
  // Imágenes existentes del backend
  const [galleryExisting, setGalleryExisting] = useState<
    Array<{ id: number; image_url: string; sort_order: number; toDelete?: boolean }>
  >([])

  const [previewImage, setPreviewImage] = useState<string | null>(null)

  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    mode: 'onChange',
    defaultValues: {
      name: '', barcode: '', description: '',
      categoryId: '', departmentId: '', productTypeId: '', unitId: '',
      satProductKeyId: '', costWithoutTaxes: '', costWithTaxes: '',
      profitPercentage: '30', taxId: '', is_active: true, pricingTiers: [],
    },
  })

  const { fields: pricingTiers, append: appendTier, remove: removeTier } = useFieldArray({
    control, name: 'pricingTiers',
  })

  // ── Cargar detalle del producto ───────────────────────────────────
  const { data: product, isLoading: isLoadingProduct, dataUpdatedAt } = useQuery({
    queryKey: ['product-edit', productId],
    queryFn: () => productService.getProduct(productId!),
    enabled: open && productId !== null,
    // No re-fetches en background mientras el modal está abierto
    staleTime: 5 * 60 * 1000,
    gcTime: 0,
  })

  // ── Impuestos disponibles ─────────────────────────────────────────────────
  const { data: taxesResponse } = useQuery({
    queryKey: ['taxes', 'all'],
    queryFn: () => taxService.getTaxes({ all: true }),
  })
  const taxesList: any[] = React.useMemo(() => {
    if (!taxesResponse) return []
    if (Array.isArray(taxesResponse)) return taxesResponse
    if (Array.isArray(taxesResponse.data)) return taxesResponse.data
    if (Array.isArray(taxesResponse.data?.data)) return taxesResponse.data.data
    return []
  }, [taxesResponse])

  // ── Rellenar form cuando llegan datos ────────────────────────────────────
  useEffect(() => {
    // Solo poblar si:
    // 1. El producto está disponible
    // 2. El modal está abierto
    if (!product || !open) return

    const firstTax = product.taxes?.[0]
    const cost = product.price?.cost_without_taxes ?? 0
    const costTax = product.price?.cost_with_taxes ?? 0
    const profit = product.price?.profit_percentage ?? 30

    reset({
      name: product.name ?? '',
      barcode: product.barcode ?? '',
      description: product.description ?? '',
      categoryId: product.category ? String(product.category.id) : '',
      departmentId: product.department ? String(product.department.id) : '',
      productTypeId: product.product_type ? String(product.product_type.id) : '',
      unitId: product.unit ? String(product.unit.id) : '',
      satProductKeyId: product.sat_product_key ? String(product.sat_product_key.id) : '',
      costWithoutTaxes: String(cost),
      costWithTaxes: String(costTax),
      profitPercentage: String(profit),
      taxId: firstTax ? String(firstTax.id) : '',
      is_active: product.is_active ?? true,
      pricingTiers: (product.price_tiers ?? []).map((t) => ({
        minQuantity: t.min_quantity,
        profitPercentage: t.profit_percentage,
        discountPercentage: t.discount_percentage,
        price: '', priceWithTax: '',
      })),
    })

    // Imagen hero existente
    setHeroImage(null)
    setRemoveExistingHero(false)
    setHeroImageExisting(product.hero_image ?? null)

    // Galería existente
    setGalleryNew([])
    setGalleryExisting(
      (product.images ?? [])
        .filter((img) => !img.is_primary)
        .map((img) => ({ id: img.id, image_url: img.image_url, sort_order: img.sort_order }))
    )
  }, [product, open, dataUpdatedAt, reset])

  // Limpiar al cerrar
  useEffect(() => {
    if (!open) {
      // Remover de caché para que la siguiente vez siempre traiga datos frescos
      queryClient.removeQueries({ queryKey: ['product-edit'] })
      reset()
      setHeroImage(null)
      setRemoveExistingHero(false)
      setHeroImageExisting(null)
      setGalleryNew([])
      setGalleryExisting([])
    }
  }, [open, reset, queryClient])

  // ── Watchers ──────────────────────────────────────────────────────────────
  const costWithoutTaxes = watch('costWithoutTaxes')
  const profitPercentage = watch('profitPercentage')
  const taxId = watch('taxId')
  const tiersValues = watch('pricingTiers') || []
  const isActiveValue = watch('is_active')
  const barcodeValue = watch('barcode')

  // ── Validación de Barcode en tiempo real ──────────────────────────────────
  useEffect(() => {
    if (!barcodeValue || !open || !productId) return
    const timer = setTimeout(async () => {
      try {
        const res = await productService.checkBarcode(barcodeValue, productId)
        if (res.exists && !res.available) {
          setError('barcode', { type: 'manual', message: 'El código de barras ya está en uso por otro producto' })
        } else {
          clearErrors('barcode')
        }
      } catch (err) {
        console.error('Error validating barcode:', err)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [barcodeValue, open, productId, setError, clearErrors])

  // ── Handlers imagen hero ──────────────────────────────────────────────────
  const handleHeroUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (heroImage) URL.revokeObjectURL(heroImage.previewUrl)
    setHeroImage(createImageFile(file))
    e.target.value = ''
  }

  const handleRemoveHero = () => {
    if (heroImage) { URL.revokeObjectURL(heroImage.previewUrl); setHeroImage(null) }
    setRemoveExistingHero(true)
    setHeroImageExisting(null)
  }

  // ── Handlers galería ──────────────────────────────────────────────────────
  const handleGalleryUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    const incoming = Array.from(e.target.files).map((file, i) =>
      createImageFile(file, galleryNew.length + i)
    )
    setGalleryNew((prev) => [...prev, ...incoming])
    e.target.value = ''
  }

  const handleRemoveNewGallery = (index: number) => {
    URL.revokeObjectURL(galleryNew[index].previewUrl)
    setGalleryNew((prev) => prev.filter((_, i) => i !== index).map((img, i) => ({ ...img, sortOrder: i })))
  }

  const toggleDeleteExisting = (id: number) => {
    setGalleryExisting((prev) =>
      prev.map((img) => img.id === id ? { ...img, toDelete: !img.toDelete } : img)
    )
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = async (data: EditFormValues) => {
    if (!productId) return
    try {
      const galleryFiles = galleryNew.map(img => img.file)
      const payload: UpdateProductPayload = {
        name: data.name,
        barcode: data.barcode,
        description: data.description,
        category_id: Number(data.categoryId),
        department_id: Number(data.departmentId),
        product_type_id: Number(data.productTypeId),
        unit_id: Number(data.unitId),
        sat_product_key_id: data.satProductKeyId ? Number(data.satProductKeyId) : undefined,
        is_active: data.is_active,
        price: {
          cost_without_taxes: Number(data.costWithoutTaxes),
          cost_with_taxes: Number(data.costWithTaxes) || undefined,
          profit_percentage: Number(data.profitPercentage),
        },
        tax_ids: data.taxId ? [Number(data.taxId)] : [],
        price_tiers: (data.pricingTiers ?? []).map((t: any) => ({
          min_quantity: Number(t.minQuantity),
          profit_percentage: Number(t.profitPercentage),
          discount_percentage: Number(t.discountPercentage),
        })),
        // Campos de imagen opcionales
        heroImageFile: heroImage?.file,
        remove_hero_image: removeExistingHero && !heroImage,
        ...(galleryFiles.length > 0 && {
          galleryFiles,
        }),
        // IDs de imágenes a eliminar de la galería
        ...(galleryExisting.some((img) => img.toDelete) && {
          delete_image_ids: galleryExisting.filter((img) => img.toDelete).map((img) => img.id),
        }),
      }

      await productService.updateProduct(productId, payload)
      // Invalidar para que la próxima vez tenga datos frescos
      queryClient.invalidateQueries({ queryKey: ['product-edit', productId] })
      toast.success('Producto actualizado correctamente')
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      toast.error('Error al actualizar el producto')
    }
  }

  // ── Imagen hero actual a mostrar ──────────────────────────────────────────
  const heroPreview = heroImage?.previewUrl ?? (removeExistingHero ? null : heroImageExisting)

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Editar Producto</DialogTitle>
            <DialogDescription className="sr-only">
              Formulario para editar la información completa del producto
            </DialogDescription>
          </DialogHeader>

          {isLoadingProduct ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-[#3B7D3B]" />
              <span className="ml-3 text-slate-600">Cargando producto...</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <Tabs defaultValue="general">
                <TabsList className="mb-4 w-full">
                  <TabsTrigger value="general" className="flex-1">General</TabsTrigger>
                  <TabsTrigger value="images" className="flex-1">Imágenes</TabsTrigger>
                  <TabsTrigger value="pricing" className="flex-1">Precios</TabsTrigger>
                  <TabsTrigger value="tiers" className="flex-1">Niveles</TabsTrigger>
                </TabsList>

                {/* ── Tab: General ────────────────────────────────────── */}
                <TabsContent value="general" className="space-y-4">
                  {/* Estado activo — switch estilo Sucursals */}
                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isActiveValue}
                      onClick={() => setValue('is_active', !isActiveValue)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActiveValue ? 'bg-green-500' : 'bg-slate-200'
                        }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActiveValue ? 'translate-x-2' : '-translate-x-2'
                          }`}
                      />
                    </button>
                    <span className="text-sm font-medium text-slate-700">
                      Producto {isActiveValue ? 'activo' : 'inactivo'}
                    </span>
                    <span className={`ml-auto text-xs ${isActiveValue ? 'text-green-600' : 'text-slate-500'}`}>
                      {isActiveValue ? 'Visible en catálogo' : 'Oculto del catálogo'}
                    </span>
                  </div>

                  {/* Nombre y Barcode */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Código de Barras <span className="text-red-500">*</span>
                      </label>
                      <Input {...register('barcode')} placeholder="7501234567890" />
                      <FormError message={errors.barcode?.message} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Nombre <span className="text-red-500">*</span>
                      </label>
                      <Input {...register('name')} placeholder="Ej: Paracetamol 500mg" />
                      <FormError message={errors.name?.message} />
                    </div>
                  </div>

                  {/* Clasificación */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Categoría <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="categoryId"
                        control={control}
                        render={({ field }) => (
                          <CatalogSelect
                            catalog="categories"
                            selectedItem={product?.category}
                            placeholder="Seleccionar categoría"
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <FormError message={errors.categoryId?.message} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Departamento <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="departmentId"
                        control={control}
                        render={({ field }) => (
                          <CatalogSelect
                            catalog="departments"
                            selectedItem={product?.department}
                            placeholder="Seleccionar departamento"
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <FormError message={errors.departmentId?.message} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Tipo de Producto <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="productTypeId"
                        control={control}
                        render={({ field }) => (
                          <CatalogSelect
                            catalog="product_types"
                            selectedItem={product?.product_type}
                            placeholder="Seleccionar tipo"
                            value={field.value}
                            onChange={(obj: any) => {
                              field.onChange(obj?.id ? String(obj.id) : obj)
                            }}
                          />
                        )}
                      />
                      <FormError message={errors.productTypeId?.message} />
                    </div>
                  </div>

                  {/* Unidad y SAT */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Unidad de Medida <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="unitId"
                        control={control}
                        render={({ field }) => (
                          <CatalogSelect
                            catalog="units"
                            selectedItem={product?.unit}
                            placeholder="Seleccionar unidad"
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <FormError message={errors.unitId?.message} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Clave SAT</label>
                      <Controller
                        name="satProductKeyId"
                        control={control}
                        render={({ field }) => (
                          <CatalogSelect
                            catalog="sat_product_keys"
                            selectedItem={product?.sat_product_key}
                            placeholder="Buscar clave SAT..."
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Descripción */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Descripción</label>
                    <Textarea {...register('description')} rows={3} placeholder="Detalles adicionales del producto..." />
                  </div>
                </TabsContent>

                {/* ── Tab: Imágenes ────────────────────────────────────────── */}
                <TabsContent value="images" className="space-y-6">
                  {/* Imagen principal */}
                  <div className="flex flex-col space-y-2 items-start">
                    <label className="text-sm font-medium text-slate-700">
                      Imagen Principal
                      <span className="ml-1 text-xs font-normal text-slate-500">(opcional)</span>
                    </label>

                    {heroPreview ? (
                      <div className="group relative inline-block">
                        <img
                          src={heroPreview}
                          alt="Imagen principal"
                          className="h-40 w-40 rounded-lg border border-slate-200 object-cover shadow-sm cursor-pointer"
                          onClick={() => setPreviewImage(heroPreview)}
                        />
                        <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                          <button
                            type="button"
                            onClick={() => setPreviewImage(heroPreview)}
                            className="rounded-full bg-white/90 p-1.5 shadow transition hover:bg-white"
                            title="Ver imagen"
                          >
                            <Eye className="h-4 w-4 text-slate-700" />
                          </button>
                          <label
                            htmlFor="hero-image-replace"
                            className="cursor-pointer rounded-full bg-white/90 p-1.5 shadow transition hover:bg-white"
                            title="Cambiar imagen"
                          >
                            <Upload className="h-4 w-4 text-slate-700" />
                          </label>
                          <button
                            type="button"
                            onClick={handleRemoveHero}
                            className="rounded-full bg-red-500/90 p-1.5 shadow transition hover:bg-red-600"
                            title="Eliminar imagen"
                          >
                            <X className="h-4 w-4 text-white" />
                          </button>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleHeroUpload}
                          className="hidden"
                          id="hero-image-replace"
                        />
                      </div>
                    ) : (
                      <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center transition hover:border-slate-300">
                        <input
                          id="hero-image"
                          type="file"
                          accept="image/*"
                          onChange={handleHeroUpload}
                          className="hidden"
                        />
                        <label htmlFor="hero-image" className="cursor-pointer">
                          <ImageIcon className="mx-auto mb-2 h-10 w-10 text-slate-400" />
                          <p className="text-sm font-medium text-slate-600">
                            Haz clic para subir la imagen principal
                          </p>
                          <p className="mt-1 text-xs text-slate-500">PNG, JPG hasta 5 MB</p>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Galería existente */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Galería de Imágenes
                    </label>

                    <div className="flex flex-wrap gap-3">
                      {/* Imágenes del servidor */}
                      {galleryExisting.map((img) => (
                        <div key={img.id} className={`group relative ${img.toDelete ? 'opacity-50 grayscale' : ''}`}>
                          <img
                            src={img.image_url}
                            alt="Galería"
                            className="h-24 w-24 rounded-lg border border-slate-200 object-cover shadow-sm cursor-pointer"
                            onClick={() => !img.toDelete && setPreviewImage(img.image_url)}
                          />
                          <div className="absolute inset-0 flex items-center justify-center gap-1 rounded-lg bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                            {!img.toDelete && (
                              <button
                                type="button"
                                onClick={() => setPreviewImage(img.image_url)}
                                className="rounded-full bg-white/90 p-1 shadow transition hover:bg-white"
                                title="Ver imagen"
                              >
                                <Eye className="h-3.5 w-3.5 text-slate-700" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => toggleDeleteExisting(img.id)}
                              className={`rounded-full p-1 shadow transition ${img.toDelete ? 'bg-green-500/90 hover:bg-green-600' : 'bg-red-500/90 hover:bg-red-600'}`}
                              title={img.toDelete ? 'Deshacer eliminación' : 'Eliminar'}
                            >
                              {img.toDelete ? <Plus className="h-3.5 w-3.5 text-white" /> : <Trash2 className="h-3.5 w-3.5 text-white" />}
                            </button>
                          </div>
                          {img.toDelete && (
                            <span className="absolute bottom-1 right-1 rounded bg-red-600 px-1 text-[10px] text-white">
                              Se eliminará
                            </span>
                          )}
                        </div>
                      ))}

                      {/* Imágenes nuevas subidas */}
                      {galleryNew.map((img, index) => (
                        <div key={`new-${index}`} className="group relative">
                          <img
                            src={img.previewUrl}
                            alt={`Nueva ${index + 1}`}
                            className="h-24 w-24 rounded-lg border border-slate-200 object-cover shadow-sm cursor-pointer border-green-400"
                            onClick={() => setPreviewImage(img.previewUrl)}
                          />
                          <div className="absolute inset-0 flex items-center justify-center gap-1 rounded-lg bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                            <button
                              type="button"
                              onClick={() => setPreviewImage(img.previewUrl)}
                              className="rounded-full bg-white/90 p-1 shadow transition hover:bg-white"
                              title="Ver imagen"
                            >
                              <Eye className="h-3.5 w-3.5 text-slate-700" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveNewGallery(index)}
                              className="rounded-full bg-red-500/90 p-1 shadow transition hover:bg-red-600"
                              title="Eliminar"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-white" />
                            </button>
                          </div>
                          <span className="absolute bottom-1 right-1 rounded bg-green-600 px-1 text-[10px] text-white">
                            Nueva
                          </span>
                        </div>
                      ))}

                      {/* Botón para subir más */}
                      <div className="flex h-24 w-24 flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 transition hover:border-slate-300">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleGalleryUpload}
                          className="hidden"
                          id="gallery-images-new"
                        />
                        <label
                          htmlFor="gallery-images-new"
                          className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-1"
                        >
                          <Plus className="h-6 w-6 text-slate-400" />
                          <span className="text-[11px] text-slate-500">Agregar</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* ── Tab: Precios ─────────────────────────────────────────── */}
                <TabsContent value="pricing" className="space-y-4">

                  {/* Impuesto */}
                  <fieldset className="space-y-3 rounded-lg bg-slate-50 p-4">
                    <legend className="sr-only">Impuestos</legend>
                    <h4 className="text-sm font-medium text-slate-900">
                      Impuesto para Precio Final al Público
                    </h4>
                    <p className="text-xs text-slate-600">
                      Selecciona el impuesto que aplica a este producto
                    </p>
                    <div className="space-y-2">
                      {taxesList.map((tax) => (
                        <div key={tax.id} className="flex items-center gap-3">
                          <Checkbox
                            id={`edit-tax-${tax.id}`}
                            checked={taxId === String(tax.id)}
                            onCheckedChange={(checked) => {
                              const val = checked ? String(tax.id) : ''
                              const calc = recalculateCosts(costWithoutTaxes || '', taxId || '', 'tax', val, taxesList)
                              setValue('taxId', val, { shouldValidate: true })
                              if (calc.costWithTaxes !== undefined) setValue('costWithTaxes', calc.costWithTaxes)
                            }}
                          />
                          <label
                            htmlFor={`edit-tax-${tax.id}`}
                            className="cursor-pointer text-sm text-slate-700"
                          >
                            Agregar {tax.name} ({tax.percentage}%) al precio final
                          </label>
                        </div>
                      ))}
                    </div>
                    <FormError message={errors.taxId?.message} />
                  </fieldset>

                  {/* Costos */}
                  <fieldset className="space-y-3 rounded-lg bg-blue-50 p-4">
                    <legend className="sr-only">Costos del Producto</legend>
                    <h4 className="text-sm font-medium text-blue-900">Costo del Producto</h4>
                    <p className="text-xs text-blue-700">
                      Ingresa el costo con o sin impuesto; el otro se calculará automáticamente
                    </p>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <label htmlFor="edit-cost-without" className="text-sm font-medium text-blue-900">
                          Costo sin Impuesto <span className="text-red-600">*</span>
                        </label>
                        <Input
                          id="edit-cost-without"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          {...register('costWithoutTaxes', {
                            onChange: (e) => {
                              const calc = recalculateCosts(costWithoutTaxes || '', taxId || '', 'without', e.target.value, taxesList)
                              if (calc.costWithTaxes !== undefined) setValue('costWithTaxes', calc.costWithTaxes)
                            },
                          })}
                        />
                        <FormError message={errors.costWithoutTaxes?.message} />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="edit-cost-with" className="text-sm font-medium text-blue-900">
                          Costo con Impuesto
                        </label>
                        <Input
                          id="edit-cost-with"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          {...register('costWithTaxes', {
                            onChange: (e) => {
                              const calc = recalculateCosts(costWithoutTaxes || '', taxId || '', 'with', e.target.value, taxesList)
                              if (calc.costWithoutTaxes !== undefined) setValue('costWithoutTaxes', calc.costWithoutTaxes)
                            },
                          })}
                        />
                      </div>
                    </div>
                    {!taxId && (
                      <p className="rounded bg-orange-100 p-2 text-xs text-orange-700">
                        Sin impuesto seleccionado: costo sin impuesto y con impuesto serán iguales
                      </p>
                    )}
                  </fieldset>

                  {/* Utilidad */}
                  <div className="space-y-2">
                    <label htmlFor="edit-profit" className="text-sm font-medium text-slate-700">
                      Porcentaje de Utilidad <span className="text-red-600">*</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="edit-profit"
                        type="number"
                        min={0}
                        max={500}
                        step={1}
                        placeholder="30"
                        className="flex-1"
                        {...register('profitPercentage')}
                      />
                      <span className="text-2xl font-semibold text-blue-700">
                        {profitPercentage || 0}%
                      </span>
                    </div>
                    <p className="text-xs text-slate-600">
                      Porcentaje de ganancia sobre el costo sin impuesto
                    </p>
                    <FormError message={errors.profitPercentage?.message} />
                  </div>

                  {/* Resumen de precio en tiempo real */}
                  {costWithoutTaxes && profitPercentage && (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-live="polite" role="status">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="mb-1 text-xs text-slate-600">Precio de venta base (sin imp.)</p>
                        <p className="text-xl font-semibold text-slate-800">
                          ${calculateSalePrice(costWithoutTaxes, profitPercentage)}
                        </p>
                      </div>
                      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                        <p className="mb-1 text-xs text-green-700">Precio final al público</p>
                        <p className="text-2xl font-semibold text-green-700">
                          ${calculateFinalPrice(costWithoutTaxes || '', profitPercentage || '', taxId || '', taxesList)}
                        </p>
                        <div className="mt-1 space-y-0.5 text-xs text-green-600">
                          <p>Base: ${calculateSalePrice(costWithoutTaxes, profitPercentage)}</p>
                          {taxId && taxesList.find((t: any) => String(t.id) === taxId) && (() => {
                            const selectedTax = taxesList.find((t: any) => String(t.id) === taxId)
                            return (
                              <p>
                                + {selectedTax.name} ({selectedTax.percentage}%): $
                                {(parseFloat(calculateSalePrice(costWithoutTaxes, profitPercentage)) * (Number(selectedTax.percentage) / 100)).toFixed(2)}
                              </p>
                            )
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* ── Tab: Niveles de Precio ─────────────────────────── */}
                <TabsContent value="tiers" className="space-y-4">
                  <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-sm font-medium text-purple-900">Precios por Cantidad</h5>
                        <p className="mt-0.5 text-xs text-purple-700">Descuentos o utilidades distintas según cantidad mínima</p>
                      </div>
                      <Button size="sm" variant="outline" type="button" onClick={() => appendTier({ minQuantity: 1, profitPercentage: 0, discountPercentage: 0, price: '', priceWithTax: '' })}>
                        <Plus className="mr-2 h-4 w-4" /> Agregar Nivel
                      </Button>
                    </div>
                    {pricingTiers.length > 0 ? (
                      <div className="space-y-2">
                        {pricingTiers.map((tier, index) => {
                          const currentTierVal = tiersValues[index] || tier
                          const tierCalc = calculateTierPrices(
                            costWithoutTaxes || '',
                            parseFloat(currentTierVal.profitPercentage) || 0,
                            parseFloat(currentTierVal.discountPercentage) || 0,
                            taxId || '',
                            taxesList
                          )
                          return (
                            <div key={tier.id} className="grid grid-cols-5 items-end gap-2 rounded-lg bg-white p-3">
                              <div className="space-y-1">
                                <label className="text-xs text-slate-600">Cant. mínima</label>
                                <Input type="number" min={1} {...register(`pricingTiers.${index}.minQuantity` as const)} className="h-8" />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs text-slate-600">Utilidad %</label>
                                <Input type="number" min={0} max={500} {...register(`pricingTiers.${index}.profitPercentage` as const)} className="h-8" />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs text-slate-600">Descuento %</label>
                                <Input type="number" min={0} max={100} {...register(`pricingTiers.${index}.discountPercentage` as const)} className="h-8" />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs text-slate-600">Precio final</label>
                                <div className="flex h-8 items-center rounded-md bg-green-50 px-2 text-xs font-medium text-green-700">
                                  {tierCalc.finalPrice ? `$${tierCalc.finalPrice}` : '—'}
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button size="sm" variant="ghost" type="button" onClick={() => removeTier(index)} className="h-8 w-8 p-0 text-red-500 hover:bg-red-50">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="py-2 text-center text-xs text-purple-600">Sin niveles configurados.</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter className="border-t border-slate-200 pt-4">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting} className="bg-[#3B7D3B] hover:bg-[#2d6a2d]">
                  {isSubmitting ? <DiscreteLoader /> : <><Save className="mr-2 h-4 w-4" /> Guardar Cambios</>}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ImagePreviewModal
        open={previewImage !== null}
        onOpenChange={(open) => !open && setPreviewImage(null)}
        imageUrl={previewImage}
      />
    </>
  )
}
